/**
 * JP Holidays annotator for match date labels.
 *
 * Adds "・祝" (public holiday) or "・振休" (substitute holiday) to weekday labels like "(火)".
 *
 * Expected markup:
 * - An ancestor element with attribute: data-game-date="YYYY-MM-DD"
 * - A target element with attribute: data-jp-holiday-weekday containing "(火)" etc.
 */
(() => {
  if (window.__jpHolidaysInitialized) return;
  window.__jpHolidaysInitialized = true;

  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function ymdKey(d) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }

  function parseYmd(ymd) {
    // Use UTC to avoid timezone date drift.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim());
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const da = Number(m[3]);
    const d = new Date(Date.UTC(y, mo - 1, da));
    // Validate round-trip.
    if (
      d.getUTCFullYear() !== y ||
      d.getUTCMonth() !== mo - 1 ||
      d.getUTCDate() !== da
    ) {
      return null;
    }
    return d;
  }

  function utcToLocalDate(utcDate) {
    // Create local Date at midnight for day-of-week calcs using local calendar.
    return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
  }

  function nthWeekdayOfMonth(year, monthIndex /* 0-11 */, weekday /* 0 Sun */, nth) {
    const first = new Date(year, monthIndex, 1);
    const firstDow = first.getDay();
    const delta = (weekday - firstDow + 7) % 7;
    const day = 1 + delta + (nth - 1) * 7;
    return new Date(year, monthIndex, day);
  }

  // Approximation formulas used widely for JP equinox holidays.
  function vernalEquinoxDay(year) {
    // 1980-2099
    return Math.floor(20.8431 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
  }
  function autumnalEquinoxDay(year) {
    // 1980-2099
    return Math.floor(23.2488 + 0.242194 * (year - 1980) - Math.floor((year - 1980) / 4));
  }

  function buildBaseHolidaysForYear(year) {
    // Returns Map<YYYY-MM-DD, "祝"> for base national holidays (not including substitute/citizen).
    const map = new Map();
    const add = (m, d) => {
      const dt = new Date(year, m - 1, d);
      map.set(ymdKey(dt), '祝');
    };

    // Fixed-date holidays (current-law set; ignores historical changes pre-2000s)
    add(1, 1);   // New Year's Day
    add(2, 11);  // National Foundation Day
    add(2, 23);  // Emperor's Birthday
    add(4, 29);  // Showa Day
    add(5, 3);   // Constitution Memorial Day
    add(5, 4);   // Greenery Day
    add(5, 5);   // Children's Day
    add(8, 11);  // Mountain Day
    add(11, 3);  // Culture Day
    add(11, 23); // Labor Thanksgiving Day

    // Happy Monday holidays
    // Coming of Age Day: 2nd Monday of January
    map.set(ymdKey(nthWeekdayOfMonth(year, 0, 1, 2)), '祝');
    // Marine Day: 3rd Monday of July
    map.set(ymdKey(nthWeekdayOfMonth(year, 6, 1, 3)), '祝');
    // Respect for the Aged Day: 3rd Monday of September
    map.set(ymdKey(nthWeekdayOfMonth(year, 8, 1, 3)), '祝');
    // Sports Day: 2nd Monday of October
    map.set(ymdKey(nthWeekdayOfMonth(year, 9, 1, 2)), '祝');

    // Equinoxes
    add(3, vernalEquinoxDay(year));
    add(9, autumnalEquinoxDay(year));

    return map;
  }

  function buildHolidayKindsForYear(year) {
    // Map<YYYY-MM-DD, "祝" | "振休">
    const base = buildBaseHolidaysForYear(year);
    const kinds = new Map(base); // start with base as "祝"

    // Substitute holidays (振替休日):
    // If holiday falls on Sunday, next non-holiday weekday becomes substitute.
    // Note: if that next day is already a holiday, continue forward.
    for (const [key] of base) {
      const d = parseYmd(key);
      if (!d) continue;
      const local = utcToLocalDate(d);
      if (local.getDay() !== 0) continue; // Sunday only

      let cursor = new Date(local.getFullYear(), local.getMonth(), local.getDate() + 1);
      while (kinds.has(ymdKey(cursor))) {
        cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
      }
      kinds.set(ymdKey(cursor), '振休');
    }

    // Citizen's holiday (国民の休日):
    // A day between two holidays becomes a holiday (treated as "祝" for suffix purposes).
    // We apply once; this is sufficient for typical cases.
    const allKeys = Array.from(kinds.keys()).sort();
    if (allKeys.length) {
      const start = parseYmd(allKeys[0]);
      const end = parseYmd(allKeys[allKeys.length - 1]);
      if (start && end) {
        const startLocal = utcToLocalDate(start);
        const endLocal = utcToLocalDate(end);
        for (
          let cur = new Date(startLocal.getFullYear(), startLocal.getMonth(), startLocal.getDate());
          cur <= endLocal;
          cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1)
        ) {
          const prev = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() - 1);
          const next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
          const kCur = ymdKey(cur);
          if (kinds.has(kCur)) continue;
          if (kinds.has(ymdKey(prev)) && kinds.has(ymdKey(next))) {
            kinds.set(kCur, '祝');
          }
        }
      }
    }

    return kinds;
  }

  const yearCache = new Map(); // year -> Map<key, kind>
  function getHolidayKind(utcDate) {
    const local = utcToLocalDate(utcDate);
    const year = local.getFullYear();
    if (!yearCache.has(year)) yearCache.set(year, buildHolidayKindsForYear(year));
    const kinds = yearCache.get(year);
    return kinds.get(ymdKey(local)) || '';
  }

  function ensureAnnotated(el) {
    const wrapper = el.closest('[data-game-date]');
    const ymd = wrapper?.getAttribute('data-game-date');
    const dUtc = parseYmd(ymd);
    if (!dUtc) return;

    const kind = getHolidayKind(dUtc);
    if (!kind) return;

    // Apply holiday color class to date + weekday labels
    wrapper
      .querySelectorAll(
        '.games-match-item-horizontal__date-label, .games-match-item-horizontal__day-label'
      )
      .forEach((node) => {
      node.classList.remove('weekday-color', 'saturday-color');
      node.classList.add('sunday-holiday-color');
    });

    const text = (el.textContent || '').trim(); // "(火)"
    // Already annotated?
    if (text.includes('・祝') || text.includes('・振休')) return;

    const suffix = kind === '振休' ? '・振休' : '・祝';
    // Insert before closing paren if present.
    if (text.endsWith(')')) {
      el.textContent = text.slice(0, -1) + suffix + ')';
    } else {
      el.textContent = text + suffix;
    }
  }

  function run() {
    document
      .querySelectorAll('.games-match-item-horizontal__day-label')
      .forEach(ensureAnnotated);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

